import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ADMIN_BASE } from '../../utils/hosts';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { ImageField } from '../ui/ImageField';
import { NewsletterPreview } from '../newsletter/NewsletterPreview';
import { emailImage, mailFrame, paragraphs, row } from '../newsletter/newsletterHtml';
import { useMailFrame } from './useMailFrame';
import { palettes } from '../marketing/marketingOptions';
import { inputCls } from '../ui/styles';
import { brandColors as c } from '../../data/brandColors';
import type { MailSettingsState } from './useMailSettings';
import type { MailSettings } from '../../core/types/mail';

/** Tabblad Huisstijl & afzender: geldt voor de automatische mails én de nieuwsbrief. */
export function MailBrandSettings({
  state,
  settings
}: {
  state: MailSettingsState;
  settings: MailSettings;
}) {
  const {
    brand
  } = settings;
  const frame = useMailFrame(brand);
  const footer = frame.brand?.footer ?? brand.footer;
  const setFooter = (patch: Partial<MailSettings['brand']['footer']>) => state.setBrand({
    footer: {
      ...brand.footer,
      ...patch
    }
  });
  const html = useMemo(() => mailFrame({
    subject: 'Voorbeeld',
    preheader: 'Zo ziet elke mail eruit',
    body: row(paragraphs('Hoi Eva,\n\nZo zien al je mails eruit: de nieuwsbrief en de mails die vanzelf gaan. Alleen de tekst verschilt.', c.ink)),
    kind: 'newsletter'
  }, frame), [frame]);
  return <div className="grid gap-6 lg:grid-cols-12" data-id="element-2565" data-mp="1qdale6zpusyc">
      <div className="min-w-0 space-y-6 lg:col-span-7" data-id="element-2566" data-mp="1v5btrhtrjzj3">
        <Panel title="Afzender" description="Zo staat je naam in de inbox. Antwoorden komen binnen op het antwoordadres." data-id="element-2567" data-mp="1v5tzr6689s47">
          <div className="grid gap-5 sm:grid-cols-2" data-id="element-2568" data-mp="206hm3ejihks3">
            <Field label="Afzendernaam" hint="Persoonlijk werkt het best, bijv. “Sarah van Beauty Bewust”." data-id="element-2569" data-mp="254j7i64etw6q">
              <input value={brand.senderName} onChange={e => state.setBrand({
              senderName: e.target.value
            })} className={inputCls} data-id="element-2570" data-mp="295wdcg7i5zdn" />
            </Field>
            <Field label="Antwoordadres" data-id="element-2571" data-mp="518muf4df96l">
              <input type="email" value={brand.replyTo} onChange={e => state.setBrand({
              replyTo: e.target.value
            })} className={inputCls} autoComplete="off" data-id="element-2572" data-mp="52jn44ylxysy" />
            </Field>
          </div>
        </Panel>

        <Panel title="Logo en kleur" description="Bovenaan elke mail. Zonder logo staat je salonnaam er in de huisletter." data-id="element-2573" data-mp="e0dindojsof2">
          <div className="space-y-5" data-id="element-2574" data-mp="ssxf5vh6zxnj">
            <Field label="Logo" as="div" hint="Liggend, op een transparante of lichte achtergrond. Wordt 44 px hoog getoond." data-id="element-2575" data-mp="ns9juxlxkuni">
              <ImageField value={brand.logo} onChange={logo => state.setBrand({
              logo
            })} fit="contain" aspect="aspect-[3/1]" data-id="element-2576" data-mp="11q7w9xgoyh0d" />
            </Field>
            {brand.logo && !emailImage(brand.logo, frame.siteUrl) && <p role="status" className="-mt-2 text-[12px] text-clay" data-id="element-2577" data-mp="33j5p3tagx9lb">
                Deze upload staat alleen in deze browser; een mail kan hem niet tonen. Tot hij via de mediabibliotheek online staat, komt je salonnaam bovenaan.
              </p>}
            <Field label="Kleur van de kopband" as="div" hint="Uit de merkstijl. Donkere kleuren kleuren ook de knoppen." data-id="element-2578" data-mp="2tdw423dohb1h">
              <div role="radiogroup" aria-label="Kleur van de kopband" className="flex flex-wrap gap-2" data-id="element-2579" data-mp="2fn0zh3a7pg3p">
                {palettes.map(p => <button key={p.key} type="button" role="radio" aria-checked={brand.palette === p.key} onClick={() => state.setBrand({
                palette: p.key
              })} className={`inline-flex min-h-[40px] items-center gap-2 rounded-full px-3 text-[13px] ring-1 transition-colors duration-150 ease-soft ${brand.palette === p.key ? 'bg-ink text-shell ring-ink' : 'bg-white text-ink ring-line hover:ring-ink/30'}`} data-id="element-2580" data-mp="25pmkl83xszdi">
                    <span className="h-4 w-4 rounded-full ring-1 ring-black/10" style={{
                  background: p.bg
                }} aria-hidden="true" data-id="element-2581" data-mp="1vzj850umf6gv" />
                    {p.name}
                  </button>)}
              </div>
            </Field>
          </div>
        </Panel>

        <Panel title="Vaste voettekst" description="Onder elke mail. In de nieuwsbrief staat de afmeldlink er altijd bij; die kan niet weg (wet)." data-id="element-2582" data-mp="1lzk1j9hrhq6n">
          <div className="space-y-5" data-id="element-2583" data-mp="1d16zrhbja0mc">
            <div className="rounded-md bg-shell px-3 py-3 text-[13px]" data-id="element-2584" data-mp="1d29mdd1tpgwg">
              <p className="text-ink" data-id="element-2585" data-mp="189ozywl8y62y">{footer.address || 'Nog geen naam en adres'}</p>
              {footer.instagram && <p className="mt-0.5 break-all text-muted" data-id="element-2586" data-mp="13bx5but7w5v2">{footer.instagram}</p>}
              <p className="mt-2 text-muted" data-id="element-2587" data-mp="ycaq5fttwfjm">
                Naam, adres en Instagram komen uit{' '}
                <Link to={`${ADMIN_BASE}/instellingen?tab=salon`} className="text-ink underline underline-offset-2 hover:text-clay" data-id="element-2588" data-mp="uagu5kukvzps">
                  Instellingen › Salon & contact
                </Link>
                , zodat ze overal hetzelfde zijn.
              </p>
              {!footer.address.trim() && <p role="alert" className="mt-2 text-[12px] text-clay" data-id="element-2589" data-mp="35w7wb28l4mwi">
                  Vul daar je adres in: zonder mag een mail wettelijk niet de deur uit.
                </p>}
            </div>
            <div className="grid gap-5 sm:grid-cols-2" data-id="element-2590" data-mp="3exp9oculyahi">
              <Field label="Facebook" hint="Volledige link, of leeg." data-id="element-2591" data-mp="2i8hz1zvj1lsd">
                <input type="url" value={brand.footer.facebook} onChange={e => setFooter({
                facebook: e.target.value
              })} placeholder="https://facebook.com/…" className={inputCls} data-id="element-2592" data-mp="2i658iybpe33p" />
              </Field>
            </div>
            <Field label="Korte regel" hint="Bijv. “Vragen? Antwoord gewoon op deze mail.” Mag leeg." data-id="element-2593" data-mp="2vz39d06x0nen">
              <input value={brand.footer.note} onChange={e => setFooter({
              note: e.target.value
            })} className={inputCls} data-id="element-2594" data-mp="1ykimqklbe6ku" />
            </Field>
          </div>
        </Panel>
      </div>

      <div className="min-w-0 lg:col-span-5" data-id="element-2595" data-mp="36tzlwppa7ubl">
        <div className="lg:sticky lg:top-20" data-id="element-2596" data-mp="36sohqorxckjy">
          <NewsletterPreview html={html} subject="Voorbeeld" preheader="Zo ziet elke mail eruit" from={brand.senderName} data-id="element-2597" data-mp="31uwgv59axo3m" />
        </div>
      </div>
    </div>;
}